import { useEffect, useState } from 'react';
import { getSubscriptionStatus, type SubscriptionStatus } from '../services/api';

const CACHE_KEY = 'subscription_status_cache';
const CACHE_TTL_MS = 8 * 60 * 60 * 1000;

interface CachedStatus {
  fetchedAt: number;
  data: SubscriptionStatus;
}

const readCache = (): SubscriptionStatus | null => {
  try {
    const raw = sessionStorage.getItem(CACHE_KEY);
    if (!raw) return null;
    const cached: CachedStatus = JSON.parse(raw);
    if (Date.now() - cached.fetchedAt > CACHE_TTL_MS) {
      sessionStorage.removeItem(CACHE_KEY);
      return null;
    }
    return cached.data;
  } catch {
    return null;
  }
};

const writeCache = (data: SubscriptionStatus) => {
  try {
    sessionStorage.setItem(CACHE_KEY, JSON.stringify({ fetchedAt: Date.now(), data }));
  } catch {
    // sessionStorage may not be available; the banner will simply refetch
  }
};

export const clearSubscriptionStatusCache = () => {
  try {
    sessionStorage.removeItem(CACHE_KEY);
  } catch {
    // ignore
  }
};

export const SubscriptionBanner = () => {
  const [status, setStatus] = useState<SubscriptionStatus | null>(() => readCache());

  useEffect(() => {
    if (status) return;
    let cancelled = false;
    getSubscriptionStatus()
      .then((data) => {
        if (cancelled) return;
        writeCache(data);
        setStatus(data);
      })
      .catch(() => {
        // No status available (no session, network error…) → show nothing
      });
    return () => {
      cancelled = true;
    };
  }, [status]);

  if (!status) return null;

  if (status.status === 'pending') {
    const cardSuffix = status.card_last_four
      ? ` terminada en ${status.card_last_four}`
      : '';
    return (
      <div className="w-full bg-amber-50 border border-amber-300 text-amber-800 px-4 py-3 rounded-xl text-sm flex items-start gap-2 mt-3">
        <svg className="w-5 h-5 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v3.75m-9.303 3.376c-.866 1.5.217 3.374 1.948 3.374h14.71c1.73 0 2.813-1.874 1.948-3.374L13.949 3.378c-.866-1.5-3.032-1.5-3.898 0L2.697 16.126zM12 15.75h.007v.008H12v-.008z" />
        </svg>
        <span>
          <strong>No pudimos procesar el pago de tu suscripción.</strong>{' '}
          No te preocupes: el sistema volverá a intentar el cobro automáticamente en los
          próximos días. Te recomendamos verificar que tu tarjeta{cardSuffix} tenga fondos
          disponibles.
        </span>
      </div>
    );
  }

  if (status.status === 'inactive') {
    return (
      <div className="w-full bg-red-50 border border-red-200 text-red-600 px-4 py-3 rounded-xl text-sm flex items-start gap-2 mt-3">
        <svg className="w-5 h-5 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v3.75m0 3.75h.008v.008H12v-.008zM21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
        </svg>
        <span>
          <strong>Tu suscripción está inactiva.</strong>{' '}
          No fue posible completar el cobro después de varios intentos, o la suscripción fue
          cancelada. Para seguir usando la plataforma, por favor actualiza tu método de pago
          o contáctanos.
        </span>
      </div>
    );
  }

  return null;
};
